<script setup lang="ts">
import Cookies from 'js-cookie'
import { loadPortalLayout } from '@/utils/portalLayout'
import type { PortalLayoutMount, PortalLayoutOptions } from '@/utils/portalLayout'
import { cancelPendingPcLogin, flushPcLogin } from '@/utils/pcLogin'

const props = defineProps<{ part: 'header' | 'footer' }>()
const element = ref<HTMLElement>()
const route = useRoute()
const { locale } = useI18n()
const config = useRuntimeConfig()
let mounted: PortalLayoutMount | undefined
let active = false
let ready = false
let initializing = false

function renderLayout() {
  if (!active || !ready || !element.value) { return }
  mounted?.unmount()
  const pathname = route.path.replace(/^\/[a-z]{2}-[a-z]{2}/i, '').replace(/\/$/, '')
  const isPassport = ['/passport/signin', '/passport/signup'].includes(pathname)
  const options: PortalLayoutOptions = {
    language: locale.value,
    currency: Cookies.get('currency'),
    keepLogin: !isPassport,
    keepCurrency: !isPassport && pathname !== '/member/order',
    desktopOnly: true,
    loginWithReload: !isPassport,
    preRelease: !['prod', 'product'].includes(config.public.envType),
    headerBg: '#00C868',
    projectTag: 'common',
  }
  if (props.part === 'header') {
    options.showFloatingService = true
    options.showFloatingWhatsApp = true
    options.showFloatingApp = true
  }
  mounted = props.part === 'header'
    ? window._injectHPPCHeader(element.value, options)
    : window._injectHPPCFooter(element.value, options)
  if (props.part === 'header') {
    if (isPassport) { cancelPendingPcLogin() }
    else { flushPcLogin() }
  }
}

async function initializeLayout() {
  if (!active || initializing) { return }
  if (ready) {
    renderLayout()
    return
  }
  initializing = true
  try {
    await loadPortalLayout(config.public.portalLayoutBaseUrl)
    ready = true
    renderLayout()
  }
  catch (error) {
    console.error('Failed to load Hopegoo portal layout', error)
  }
  finally {
    initializing = false
  }
}

onMounted(() => {
  active = true
  return initializeLayout()
})
watch([() => route.path, locale], initializeLayout, { flush: 'post' })
onBeforeUnmount(() => {
  active = false
  mounted?.unmount()
  if (props.part === 'header') { cancelPendingPcLogin() }
})
</script>

<template>
  <div :id="part === 'header' ? 'init_header' : 'init_footer'" ref="element" />
</template>
